import React from 'react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { ROUTESLIST } from '@/static/urls';
import WordsLimitedField from '@/components/forms/words-limited-field';
import RadioField from '@/components/forms/radio-field';
import DatePickerField from '@/components/forms/date-picker';
import { arrangeData, convertDateToDb } from '@/middlewares/api';
import './index.scss';
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';

const WordsLimitedFieldFormik = formikFieldAdapter(WordsLimitedField);
const RadioFieldFormik = formikFieldAdapter(RadioField);
const DatePickerFieldFormik = formikFieldAdapter(DatePickerField);

// Deadline: nullable, valid date; stored as MM/DD/YY, backend expects Y-m-d
const deadlineSchema = yup
    .string()
    .required('Preferred clearance deadline is required')
    .test(
        'date-format',
        'Enter a valid date (MM/DD/YY)',
        (v) => !v || moment(v, 'MM/DD/YY', true).isValid()
    );

// At least one of master_sampled_used, publishing_sampled_used, mixed_sampled_used (frontend: type_of_used required)
const validationSchema = yup.object({
    description: yup
        .string()
        .required('Description of the sample used is required')
        .max(65000, 'Description must be at most 65,000 characters'),
    deadline: deadlineSchema,
    type_of_used: yup
        .string()
        .oneOf(['master', 'publish', 'both'], 'Sample Used is required.')
        .required('Sample Used is required.'),
});

const initialValuesShape = {
    description: '',
    deadline: '',
    type_of_used: '',
};

function UploadSampleStep4({ nextStep, prevStep, initialValues = {}, bgImg, savingAll }) {
    const values = {
        ...initialValuesShape,
        ...initialValues,
    };

    const stepSubmit = (data) => {
        const formData = arrangeData(data);
        formData.deadline = convertDateToDb(formData.deadline);
        if (data.type_of_used === 'master' || data.type_of_used === 'both') {
            formData.master_sampled_used = 1;
        }
        if (data.type_of_used === 'publish' || data.type_of_used === 'both') {
            formData.publishing_sampled_used = 1;
        }
        delete formData.type_of_used;
        nextStep(formData, data);
    };

    const goBack = (currentValues) => {
        prevStep(currentValues);
    };

    return (
        <section className="upload-form__sec" style={bgImg}>
            <div className="content">
                <div className="upload-form__top">
                    <h2 className="head-shadow">CLEAR SAMPLES</h2>
                    <h3 className="head">Step 4 of 4: THE SAMPLE DESCRIPTION</h3>
                </div>
                <div className="form-step__body">
                    <Formik
                        initialValues={values}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={(data) => stepSubmit(data)}
                    >
                        {({ values: currentValues, isSubmitting }) => (
                            <Form className="form-layout" noValidate>
                                <Field
                                    name="description"
                                    component={WordsLimitedFieldFormik}
                                    required={true}
                                    maxWords={150}
                                    placeholder="Tell us how you used the sample."
                                    label="Description of the Sample Used"
                                />
                                <Field
                                    name="deadline"
                                    component={DatePickerFieldFormik}
                                    required={true}
                                    placeholder="enter MM/DD/YY"
                                    label="Preferred Clearance Deadline"
                                />
                                <div className="upload-form__checks upload-form__checks_radio">
                                    <Field
                                        name="type_of_used"
                                        component={RadioFieldFormik}
                                        value="master"
                                        label={
                                            <>
                                                <span>
                                                    Master Sample Used (I've sampled directly from a
                                                    pre-existing recording)
                                                </span>
                                            </>
                                        }
                                        type="radio"
                                    />
                                    <Field
                                        name="type_of_used"
                                        component={RadioFieldFormik}
                                        value="publish"
                                        label={
                                            <>
                                                <span>
                                                    Publishing Sample Used (I've re-recorded, replayed
                                                    and/or re-sung part, or all, of another song)
                                                </span>
                                            </>
                                        }
                                        type="radio"
                                    />
                                    <Field
                                        name="type_of_used"
                                        component={RadioFieldFormik}
                                        value="both"
                                        label={
                                            <>
                                                <span>
                                                    Master & Publishing Use (I've used a significant amount
                                                    of the master and composition in my new recording.
                                                    Example: "A Boy Is a Gun" by Tyler, the Creator samples
                                                    "Bound" by Ponderosa Twins Plus One)
                                                </span>
                                            </>
                                        }
                                        type="radio"
                                    />
                                </div>

                                <div className="submit-row_txt">
                                    <p className="as-text">
                                        By clicking Next button you are agreeing to the
                                        <Link
                                            to={ROUTESLIST.STATIC_TERMS}
                                            className="link-thin"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Terms and Conditions.
                                        </Link>
                                    </p>
                                </div>
                                <div className="submit-row submit-row_txt">
                                    <button
                                        className="btn btn-second"
                                        onClick={() => goBack(currentValues)}
                                        type="button"
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="btn-red-primary"
                                        disabled={isSubmitting || savingAll}
                                        type="submit"
                                    >
                                        Next
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </section>
    );
}

export default UploadSampleStep4;
